// Where can THIS account actually spend its Gas Tank?
//
// The gas tank balance itself is cross-chain — `EstimationController` merges the
// gas-tank tokens into the fee tokens of every network ("they belong
// everywhere", estimation.ts). So the chain a user tops up ON is irrelevant;
// what matters is whether there is at least one network where the account can
// be made to pay a fee with it. That depends on the account type:
//
//   * Smart Account (v2, ZKX factory) — pays through the relayer (`hasRelayer`)
//     or, on 4337 chains, through the bundler + paymaster. See
//     `V2.getAvailableFeeOptions`.
//   * EOA — a plain EOA can NEVER pay from the gas tank (`EOA.getAvailableFeeOptions`
//     returns the native option only). It must first upgrade via EIP-7702, and
//     even then the fee option only appears when a bundler estimation with a
//     usable paymaster exists. See `EOA7702.getAvailableFeeOptions`.
//
// Keep this in sync with those three `getAvailableFeeOptions` implementations —
// this module exists so the UI can explain the same rule the estimator enforces,
// instead of silently offering a top-up whose funds could never be spent.
import { Network } from '@ambire-common/interfaces/network'

export type GasTankAccountKind = {
  /** v2 Smart Account created by the ZKX factory. */
  isSmartAccount: boolean
  /** EOA holding a key type that can sign an EIP-7702 delegation. */
  canUpgradeToSmart: boolean
}

const hasUsablePaymaster = (network: Network): boolean =>
  !!network.erc4337?.enabled && !!network.erc4337?.hasPaymaster && !!network.erc4337?.hasBundlerSupport

/** Can `kind` of account pay a fee from the gas tank on this specific network? */
export const canSpendGasTankOnNetwork = (network: Network, kind: GasTankAccountKind): boolean => {
  // Gate on `areContractsDeployed` first, for BOTH account kinds. It is the one
  // flag the client forces itself (`ZKX_SA_DEPLOYED_CHAIN_IDS` is intersected in
  // `mapRelayerNetworkConfigToAmbireNetwork`), so it survives two failure modes
  // the other flags do not: a backend advertising chains we never deployed to,
  // and — observed live — a STORED network still carrying `hasRelayer: true`
  // from an older client version, which the merge only overwrites when the
  // backend bumps `predefinedConfigVersion` (libs/networks/networks.ts).
  if (!network.areContractsDeployed) return false

  if (kind.isSmartAccount) return !!network.hasRelayer || hasUsablePaymaster(network)

  // EOA: needs the chain to support 7702 AND a paymaster to sponsor the userOp.
  return kind.canUpgradeToSmart && !!network.has7702 && hasUsablePaymaster(network)
}

/** Every network where this account could spend the gas tank right now. */
export const getGasTankSpendableNetworks = (
  networks: Network[],
  kind: GasTankAccountKind
): Network[] => networks.filter((network) => canSpendGasTankOnNetwork(network, kind))
